/**
 * Wine identity: normalisation and the Domaine + Cépage + Parcelle + Année
 * uniqueness rule.
 *
 * See specs/001-wine-cellar-tracker/spec.md FR-002/FR-003 and
 * data-model.md's "Wine" section: identity is unique within a cellar,
 * compared after normalisation (lower case, accents removed, whitespace
 * collapsed; an empty Parcelle is a value, not an absence).
 */

export interface WineIdentityFields {
  domaine: string;
  cepage: string;
  /** Optional; an empty/undefined Parcelle is itself a value for identity purposes. */
  parcelle?: string;
  annee: number;
}

/** Lower-case, strip accents, collapse and trim whitespace. Never throws on undefined/empty input. */
export function normaliseIdentityField(value: string | undefined | null): string {
  if (!value) {
    return '';
  }
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // combining diacritical marks
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/** A stable string key for a wine's identity, suitable for map/set lookups or a unique index. */
export function wineIdentityKey(fields: WineIdentityFields): string {
  return [
    normaliseIdentityField(fields.domaine),
    normaliseIdentityField(fields.cepage),
    normaliseIdentityField(fields.parcelle),
    String(fields.annee),
  ].join('|');
}

/** Whether two sets of identity fields refer to the same wine, per FR-002/FR-003. */
export function sameWineIdentity(a: WineIdentityFields, b: WineIdentityFields): boolean {
  return wineIdentityKey(a) === wineIdentityKey(b);
}
